import React, { useEffect, useState } from "react";
import CustomDataTable from "components/customs/table";
import { showDeleteModal } from "utility/helpers";
import { defaultTableOption, rowPerPage } from "constants/table";
import { SUPER_ADMIN_ACCOUNTS, ACCOUNT_EDITOR } from "utility/constants";
import useAuth from "hooks/useAuth";
import LoadingScreen from "components/customs/loading-screen";
import styled from "styled-components";
import { HeaderText } from "assets/js/Styles";
import { useNavigate } from "react-router";
import useAccounts from "hooks/useAccounts";
import useAgencies from "hooks/useAgencies";
import accountColumns from "./columns";
import AccountFilters from "./AccountFilters";

const TableSection = styled.div`
  margin-top: 16px;
`;

const Wrapper = styled.div`
  margin-top: 50px;
`;

const CardContainer = styled.div`
  padding-top: 24px;
  padding-left: 50px;
  padding-right: 50px;
  padding-bottom: 24px;
`;

const CardSection = styled.div`
  background: #ffffff;
  border-radius: 15px;
  box-shadow: 4px 9px 17px -1px #00000014;
`;

const HeaderTextSection = styled.div`
  display: grid;
  justify-content: space-between;
  grid-auto-flow: column;
  align-items: center;

  h2 {
    margin-bottom: 0;
  }

  a {
    text-decoration: none;
  }

  @media (max-width: 575.98px) {
    grid-template-columns: 1fr 50px;

    a {
      display: flex;
      justify-content: flex-end;
    }
  }
`;

const AccountsTable = () => {
  const [tableOption, setTableOption] = useState({
    ...defaultTableOption,
    isLoading: true,
    currentRowsPerPage: rowPerPage,
  });
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);

  const { user } = useAuth();

  const navigate = useNavigate();

  const { data: agencies } = useAgencies();

  const { data: accounts, refetch } = useAccounts({
    limit: tableOption.currentRowsPerPage,
    offset: tableOption.offset,
    type: selectedRole?.value ?? null,
    agency: selectedAgency?.id ?? null,
  });

  let changeSearchTimeoutID;

  const fetchData = () => {
    setTableOption({
      ...tableOption,
      isLoading: true,
    });

    let newData = accounts?.filter((d) => d?.userId !== user?.userId) ?? [];

    if (user?.type === ACCOUNT_EDITOR) {
      newData = newData.filter((d) => !SUPER_ADMIN_ACCOUNTS.includes(d.type));
    }

    setTableOption({
      ...tableOption,
      data: newData,
      totalRows: newData?.length,
      isLoading: false,
    });
  };

  const handleDeleteAction = (row) => {
    showDeleteModal({
      title: row?.firstName,
      url: `/auth/delete-user/${row?.username}`,
      callBack: () => {
        refetch();
      },
    });
  };

  const columns = accountColumns({
    agencies,
    user,
    navigate,
    handleDeleteAction,
  });

  const onChangeSearch = (event) => {
    event.persist();

    if (changeSearchTimeoutID) {
      clearTimeout(changeSearchTimeoutID);
    }

    changeSearchTimeoutID = setTimeout(() => {
      const eventValue = event.target.value;

      const res = accounts.filter(
        (data) =>
          data?.firstName?.toLowerCase()?.includes(eventValue.toLowerCase()) ||
          data?.lastName?.toLowerCase()?.includes(eventValue.toLowerCase()) ||
          data?.middleName?.toLowerCase()?.includes(eventValue.toLowerCase()) ||
          data?.cosFirstName
            ?.toLowerCase()
            ?.includes(eventValue.toLowerCase()) ||
          data?.cosLastName
            ?.toLowerCase()
            ?.includes(eventValue.toLowerCase()) ||
          data?.cosMiddleName
            ?.toLowerCase()
            ?.includes(eventValue.toLowerCase()) ||
          data?.cosName?.toLowerCase()?.includes(eventValue.toLowerCase())
      );

      setTableOption({
        ...tableOption,
        data: res,
      });
    }, 1000);
  };

  useEffect(() => {
    if (accounts && accounts.length > 0) {
      fetchData();
    }
  }, [selectedAgency, selectedRole, accounts]);

  if (!user) {
    return <LoadingScreen />;
  }

  return (
    <Wrapper>
      <CardSection>
        <CardContainer>
          <HeaderTextSection>
            <HeaderText>{"Accounts"}</HeaderText>
          </HeaderTextSection>

          <AccountFilters
            setSelectedAgency={setSelectedAgency}
            selectedAgency={selectedAgency}
            agencies={agencies}
            selectedRole={selectedRole}
            setSelectedRole={setSelectedRole}
            onChangeSearch={onChangeSearch}
            tableOption={tableOption}
          />

          <TableSection>
            <CustomDataTable
              columns={columns}
              data={tableOption.data}
              noHeader
              progressPending={tableOption.isLoading}
              height="calc(100vh - 200px)"
            />
          </TableSection>
        </CardContainer>
      </CardSection>
    </Wrapper>
  );
};

export default AccountsTable;
